<template>
  <button :class="[
    'rounded-button',
    active && 'active',
    slim && 'slim',
    tall && 'tall',
    extraTall && 'extra-tall',
    mediumwide && 'medium-wide',
    wide && 'wide',
    extrawide && 'extrawide',
    shadow && 'shadow',
    dark && 'dark',
    darker && 'darker',
    image && 'image',
    imageRight && 'image',
    text && image && 'text-and-image',
    text && imageRight && 'text-and-image-right',
    reverse && 'reverse',
    secondaryFont && 'secondary-font',
    bold && 'bold',
    semiBold && 'semi-bold',
    smallerFont && 'smaller-font',
    largerFont && 'larger-font',
    fitWidth && 'fit-width',
    fitHeight && 'fit-height',
    outline && 'outline',
    monospace && 'monospace',
    lessRound && 'less-round',
    roundedSquare && 'rounded-square',
    accent && 'accent',
    grey && 'grey',
    lessPadding && 'less-padding',
    morePadding && 'more-padding',
    imageWithoutPadding && 'image-without-padding',
    uppercase && 'uppercase',
    red && 'red',
    adjustTextAlignment && 'adjust-text-alignment',
  ]" :disabled="disabled" 
    @click="onClick"
    @keydown="($event) => handleKeyDown($event, blurOnPress)" 
    :type="type" 
    :aria-labelledby="ariaLabel" 
    :aria-describedby="text ? undefined : ariaDescription">
    <img v-if="imageRight" :src="imageRight" :class="[
      smallerImage && 'smaller-image',
      biggerGap && 'bigger-gap',
      rightIcon && 'right-icon',
      imageBorder && 'image-border',
    ]" />
    <slot class="button-text"></slot>
    <span class="text">{{ text }}</span>
    <img v-if="image" :src="image" :class="[
      smallerImage && 'smaller-image',
      biggerGap && 'bigger-gap',
      rightIcon && 'right-icon',
      imageBorder && 'image-border',
    ]" />
    <ProgressSpinner v-if="loading" strokeWidth="5" class="spinner bottom" />
  </button>
</template>

<script>
export default {
  props: {
    disabled: Boolean,
    active: Boolean,
    primary: Boolean,
    onClick: Function,
    ariaLabel: String,
    ariaDescription: String,
    enterPress: Function,
    blurOnPress: Boolean,
    image: String,
    smallerImage: Boolean,
    smallerFont: Boolean,
    largerFont: Boolean,
    text: String,
    slim: Boolean,
    mediumwide: Boolean,
    wide: Boolean,
    extrawide: Boolean,
    shadow: Boolean,
    dark: Boolean,
    darker: Boolean,
    type: String,
    reverse: Boolean,
    secondaryFont: Boolean,
    bold: Boolean,
    semiBold: Boolean,
    fitWidth: Boolean,
    fitHeight: Boolean,
    outline: Boolean,
    monospace: Boolean,
    lessRound: Boolean,
    roundedSquare: Boolean,
    accent: Boolean,
    lessPadding: Boolean,
    morePadding: Boolean,
    grey: Boolean,
    biggerGap: Boolean,
    imageWithoutPadding: Boolean,
    loading: Boolean,
    tall: Boolean,
    extraTall: Boolean,
    uppercase: Boolean,
    rightIcon: Boolean,
    red: Boolean,
    adjustTextAlignment: Boolean,
    imageRight: {
      type: String,
      default: null,
    },
    imageBorder: Boolean,
  },

  methods: {
    handleKeyDown(event) {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.target.blur();
        this.enterPress?.(event);
      }
    },
  },
};

</script>

<style lang="scss">
.rounded-button {
  @include section-sub-header;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: map-get($component-sizes, button-height);
  width: map-get($component-sizes, button-min-width);
  padding: 0 1.4rem;
  border-radius: 2.4rem;
  white-space: nowrap;
  border: none;
  outline: none;
  position: relative;

  &[disabled] {
    background-color: map-get($component-colours, button-colour-disabled);
    color: map-get($component-colours, button-font-colour-disabled);
  }

  &[hover] {
    background-color: map-get($component-colours, button-colour-disabled);
    color: map-get($component-colours, button-font-colour-disabled);
  }

  &.active {
    background-color: map-get($colours, accent-purple-3);
    color: map-get($colours, white);
  }

  &.grey {
    background-color: map-get($colours, grey-2);
    color: map-get($colours, grey-6);

    &[disabled] {
      background-color: map-get($colours, grey-1);
      color: map-get($colours, grey-4);
    }
  }

  &:focus-visible {
    box-shadow: 0 0 0 0.3rem rgba(255, 233, 254, 0.4);
    outline: solid 0.2rem rgba(255, 233, 254, 1);
    border: solid 0.1rem map-get($colours, accent-purple3);
  }

  &.image {
    padding: 0 1rem;
  }

  &.less-padding {
    padding: 0 1rem;
  }

  &.more-padding {
    padding: 0 2.4rem;
  }

  &.outline {
    border-radius: 44rem;
    border: solid 0.1rem map-get($colours, accent-purple);
  }

  &.monospace {
    text-transform: uppercase;
    letter-spacing: 0.125rem;
  }

  &.fit-width {
    width: 100%;
  }

  &.fit-height {
    height: 100%;
  }

  &.smaller-font {
    font-size: 1.2rem;
  }

  &.larger-font {
    font-size: 1.8rem;
  }

  &.dark {
    background: map-get($component-colours, button-colour-dark-50);
    color: map-get($component-colours, button-font-colour-primary);
  }

  &.darker {
    background: map-get($component-colours, button-colour-dark-2);
    color: map-get($component-colours, button-font-colour-primary);

    &.outline {
      border: 0.2rem solid map-get($component-colours, button-colour-dark-2);
      background: transparent;
      color: map-get($component-colours, button-font-colour-dark);

      &:hover,
      &:active &:not([disabled]) {
        background: map-get($component-colours, button-colour-dark-2);
        color: map-get($component-colours, button-font-colour-primary);
      }
    }
  }

  &.accent {
    background: map-get($component-colours, button-colour-accent);
    color: map-get($component-colours, button-font-colour-accent);

    &[disabled] {
      background-color: map-get($colours, grey-1);
      color: map-get($component-colours, button-font-colour-disabled);
    }
  }

  &.reverse {
    flex-direction: row-reverse;

    img {
      margin-left: 0;
      margin-right: 0.2rem;

      &.bigger-gap {
        margin-right: 0.8rem;
      }
    }
  }

  &.secondary-font {
    font-family: $font-secondary;
    letter-spacing: 0.1rem;
  }

  &.tertiary-font {
    font-family: 'Manrope';
    font-size: 16px;
    letter-spacing: 0.1rem;
  }
  
  &.bold {
    font-weight: bold;
  }

  &.semi-bold {
    font-weight: 500;
  }

  &.uppercase {
    text-transform: uppercase;
    letter-spacing: 0.125rem;
  }

  &.red {
    color: map-get($colours, white);
    background: map-get($colours, red);
  }

  // Can make text look more vertically aligned
  &.adjust-text-alignment {
    .text {
      margin-top: 0.2rem;
    }
  }

  img {
    margin-left: 1rem;

    &.smaller-image {
      height: 70%;
    }

    &.right-icon {
      position: absolute;
      right: 1.6rem;
    }

    &.image-border {
      border-radius: 2rem;
      background-color: map-get($colours, accent-purple-4);
      padding: 0.8rem;
    }
  }

  &.less-round {
    border-radius: 0.8rem;
  }

  &.rounded-square {
    border-radius: 1.8rem;
  }

  .p-progressspinner {
    height: 20px;
    width: 20px;
    margin: 0;
    margin-left: 1.2rem;
  }

  .p-progressspinner-circle {
    // Hide spinner before animation starts
    stroke: none;
    // stroke: rgba(255, 255, 255, 0.5);
    animation: p-progress-spinner-dash 1.5s ease-in-out infinite,
      p-progress-spinner-color-dark 6s ease-in-out infinite;
    -webkit-animation: p-progress-spinner-dash 1.5s ease-in-out infinite,
      p-progress-spinner-color-dark 6s ease-in-out infinite;
    animation-delay: -2.5s;
  }

}

.rounded-button.extra-tall {
  min-height: map-get($component-sizes, button-height-extra-tall);
}

.rounded-button.tall {
  min-height: map-get($component-sizes, button-height-tall);
}

.rounded-button.slim {
  min-height: map-get($component-sizes, button-height-slim);
}

.rounded-button.medium-wide {
  min-width: map-get($component-sizes, button-min-width-medium-wide);
}

.rounded-button.wide {
  min-width: map-get($component-sizes, button-min-width-wide);
}

.rounded-button.extrawide {
  min-width: map-get($component-sizes, button-min-width-extrawide);
}

.rounded-button.shadow {
  box-shadow: $small-crisp-box-shadow;
}

.rounded-button.text-and-image:not(.image-without-padding) {
  .text {
    // Balances the whitespace to make content look more centered
    margin-left: 0.4rem;
  }
}

.rounded-button.text-and-image-right:not(.image-without-padding) {
  .text {
    // Balances the whitespace to make content look more centered
    margin-left: 0.8rem;
  }
}
</style>
